import React, { useRef } from "react";
import AsyncSelect from "react-select/async";
import { getProducts } from "Requests/Products";

const DEBOUNCE_MS = 400;

export interface ProductOption {
  id: number;
  product: string;
}

interface SelectProductRulesProps {
  value: ProductOption | null;
  onChange: (product: ProductOption | null) => void;
  label?: string;
  error?: string;
  small?: boolean;
  id?: string;
  purple?: boolean;
  placeholder?: string;
  disabled?: boolean;
}

export const SelectProductRules = ({
  value,
  onChange,
  label = "",
  error = "",
  small = false,
  id = "product",
  purple = false,
  placeholder = "Selecione o produto",
  disabled = false,
}: SelectProductRulesProps) => {
  const timeoutRef = useRef<number | null>(null);

  const loadOptions = (inputValue: string): Promise<ProductOption[]> => {
    return new Promise((resolve) => {
      if (timeoutRef.current) {
        window.clearTimeout(timeoutRef.current);
      }
      timeoutRef.current = window.setTimeout(async () => {
        try {
          const response = await getProducts({
            page: 1,
            filters: { search: inputValue || "" },
            searchBudget: true,
          });
          const list = response?.data ?? [];
          const options: ProductOption[] = list.map(
            (p: { id: number; sku: string; product: string }) => ({
              id: p.id,
              product: p.product,
            })
          );
          resolve(options);
        } catch {
          resolve([]);
        } finally {
          timeoutRef.current = null;
        }
      }, DEBOUNCE_MS);
    });
  };

  return (
    <div
      className={`
            field-holder 
            ${error ? "field-holder-invalid" : ""}
            ${small ? "field-holder-small" : ""}
        `}
    >
      {label && (
        <label
          htmlFor={id}
          className={`text-gray text-semibold ${purple ? "_purple" : ""}`}
        >
          {label}
        </label>
      )}
      <AsyncSelect<ProductOption>
        cacheOptions
        defaultOptions
        isDisabled={disabled}
        loadOptions={loadOptions}
        value={value}
        onChange={(option) => onChange(option ?? null)}
        isClearable
        isSearchable
        name="product"
        inputId={id}
        placeholder={placeholder}
        classNamePrefix="react-select"
        className="react-select input _opacity-gray"
        getOptionValue={(option) => String(option.id)}
        getOptionLabel={(option) => `${option.product}`}
        loadingMessage={() => "Carregando produtos..."}
        noOptionsMessage={({ inputValue }) =>
          inputValue ? "Nenhum produto encontrado" : "Digite para buscar"
        }
      />
      {error !== "" && (
        <span
          className={`error-message ${!label ? "error-without-label" : ""}`}
        >
          {error}
        </span>
      )}
    </div>
  );
};
